import { create } from 'zustand';
import {
  ListPayments,
  CreatePayment,
  GetPayment,
  UpdatePayment,
  DeletePayment,
  ExportPayments,
  Payment,
  CreatePaymentI,
  UpdatePaymentI
} from '../api/api';
import toast from 'react-hot-toast';

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface GetPaymentsResponse {
  data: {
    payments: Payment[];
    pagination?: PaginationInfo;
  };
}

interface PaymentResponse {
  data: {
    payment: Payment;
  };
  status: boolean;
  message: string;
}

interface DeletePaymentResponse {
  status: boolean;
  message: string;
}

interface ExportPaymentsResponse {
  data: {
    payments: any[];
    total: number;
  };
  status: boolean;
  message: string;
}

interface PaymentStore {
  payments: Payment[];
  loading: boolean;
  error: string | null;
  pagination: PaginationInfo | null;
  getPayments: (
    search?: string,
    page?: number,
    limit?: number,
    paymentTypeId?: string,
    familyId?: string,
    memberId?: string,
    status?: string,
    startDate?: string,
    endDate?: string
  ) => Promise<void>;
  createPayment: (payment: CreatePaymentI) => Promise<void>;
  getPaymentById: (id: string) => Promise<Payment | null>;
  updatePayment: (id: string, payment: UpdatePaymentI) => Promise<void>;
  deletePayment: (id: string) => Promise<void>;
  exportPayments: (
    paymentTypeId?: string,
    familyId?: string,
    memberId?: string,
    status?: string,
    startDate?: string,
    endDate?: string
  ) => Promise<any[] | null>;
}

export const usePaymentStore = create<PaymentStore>((set, get) => ({
  payments: [] as Payment[],
  loading: false,
  error: null,
  pagination: null,

  getPayments: async (
    search?: string,
    page?: number,
    limit?: number,
    paymentTypeId?: string,
    familyId?: string,
    memberId?: string,
    status?: string,
    startDate?: string,
    endDate?: string
  ) => {
    set({ loading: true, error: null });
    const { data, error } = await ListPayments<GetPaymentsResponse>(
      search,
      page,
      limit,
      paymentTypeId,
      familyId,
      memberId,
      status,
      startDate,
      endDate
    );

    if (error) {
      set({ loading: false, error: error as string });
      return;
    }

    set({
      payments: data.data.payments || [],
      pagination: data.data.pagination || null,
      loading: false,
    });
  },

  createPayment: async (payment: CreatePaymentI) => {
    set({ loading: true, error: null });
    const { data, error } = await CreatePayment<PaymentResponse>(payment);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to create payment' });
      return;
    }

    const payments = get().payments;
    set({ payments: [data.data.payment, ...payments], loading: false });
    toast.success('Payment created successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getPayments(
      undefined,
      currentPagination?.page,
      currentPagination?.limit
    );
  },

  getPaymentById: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await GetPayment<PaymentResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to get payment' });
      return null;
    }

    set({ loading: false });
    return data.data.payment;
  },

  updatePayment: async (id: string, payment: UpdatePaymentI) => {
    set({ loading: true, error: null });
    const { data, error } = await UpdatePayment<PaymentResponse>(id, payment);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to update payment' });
      return;
    }

    const payments = get().payments;
    const updatedPayments = payments.map((p) => (p._id === id ? { ...p, ...data.data.payment } : p));
    set({ payments: updatedPayments, loading: false });
    toast.success('Payment updated successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getPayments(
      undefined,
      currentPagination?.page,
      currentPagination?.limit
    );
  },

  deletePayment: async (id: string) => {
    set({ loading: true, error: null });
    const { data, error } = await DeletePayment<DeletePaymentResponse>(id);

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to delete payment' });
      return;
    }

    const payments = get().payments.filter((p) => p._id !== id);
    set({ payments, loading: false });
    toast.success('Payment deleted successfully');
    // Refresh the list with current pagination
    const currentPagination = get().pagination;
    get().getPayments(
      undefined,
      currentPagination?.page,
      currentPagination?.limit
    );
  },

  exportPayments: async (
    paymentTypeId?: string,
    familyId?: string,
    memberId?: string,
    status?: string,
    startDate?: string,
    endDate?: string
  ) => {
    set({ loading: true, error: null });
    const { data, error } = await ExportPayments<ExportPaymentsResponse>(
      paymentTypeId,
      familyId,
      memberId,
      status,
      startDate,
      endDate
    );

    if (error || !data.status) {
      set({ loading: false, error: (error as string) || 'Failed to export payments' });
      return null;
    }

    set({ loading: false });
    toast.success('Payments exported successfully');
    return data.data.payments || [];
  },
}));

export type { Payment, CreatePaymentI, UpdatePaymentI };
